import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import WorkOrderForm from "../machinery-register/WorkOrderForm";
import { ApiURL, baseURL } from "../../../../services/apiConstants";
import { post } from "../../../../services/services";
import { ErrorToast, SuccessToast } from "../../../../widgets/AppToast";
import { useLocation } from "react-router-dom";
import { Form, Card, Descriptions, Spin, Empty, Row, Col } from "antd";
import { FileImageOutlined, FileTextOutlined } from "@ant-design/icons";

const formatDate = (date) => {
  if (!date) return "N/A";
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "N/A";
};

const appendIfValue = (formData, key, value) => {
  if (value !== undefined && value !== null && value !== "") {
    formData.append(key, value);
  }
};

const getResponseRecord = (res) =>
  res?.data?.data ||
  res?.data ||
  res?.response_data ||
  res?.task ||
  res?.job_task ||
  null;

const getPrimitiveId = (value) =>
  value && typeof value === "object" ? value.id : value;

const getJobId = (record) =>
  record?.job_id || record?.job?.id || record?.job_register_id || record?.job_register?.id || getPrimitiveId(record?.job);

const getJobCode = (record) => {
  const code = record?.job_code || record?.job?.code || record?.job_register?.code;

  if (code) return code;

  return record?.code ? String(record.code).replace(/-W\d+$/i, "") : "";
};

const getSingleRecord = (record, matcher) => {
  if (!Array.isArray(record)) return record;

  return record.find(matcher) || record[0] || null;
};

const getAttachmentUrl = (file) => {
  const rawFileUrl =
    file?.file ||
    file?.attachment ||
    file?.file_url ||
    file?.url ||
    "";

  if (!rawFileUrl) return "";

  return rawFileUrl.startsWith("http")
    ? rawFileUrl
    : `${baseURL}${rawFileUrl}`;
};

const isOverdue = (record) => {
  const status = String(record?.status || "").toLowerCase();

  if (status === "completed") return false;

  if (status === "overdue") return true;

  const dueDate = record?.due_date || record?.next_due_date;

  return dueDate
    ? dayjs().isAfter(dayjs(dueDate), "day")
    : false;
};

const WorkOrderReport = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const passedWorkOrder =
    location?.state?.workOrder;
  const isViewMode =
    location?.state?.mode === "view";

  const userData = JSON.parse(localStorage.getItem("user_data"));
  const companyId =
    location?.state?.companyId ||
    passedWorkOrder?.company_id ||
    passedWorkOrder?.company?.id ||
    userData?.data?.company?.company_id;
  const shipId =
    userData?.data?.ship_details?.ship_id ||
    location?.state?.shipId ||
    passedWorkOrder?.ship_id ||
    passedWorkOrder?.ship?.id;
  const machineryId =
    location?.state?.machineryId ||
    passedWorkOrder?.machinery_id ||
    passedWorkOrder?.machinary_id ||
    passedWorkOrder?.job_machinery_id ||
    passedWorkOrder?.job?.machinery_id ||
    passedWorkOrder?.job?.machinary_id;
  const workOrderId = location?.state?.workOrderId || id;
  const jobId = getPrimitiveId(location?.state?.jobId) || getJobId(passedWorkOrder);
  const jobCode = getJobCode(passedWorkOrder);

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [submitLoader, setSubmitLoader] = useState(false);
  const [selectedJobList, setselectedJobList] = useState(null);

  const fetchWorkOrder = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    try {
      const formData = new FormData();
      let res;
      let record;

      if (isViewMode) {
        formData.append("work_order_id", workOrderId);
        appendIfValue(formData, "company_id", companyId);
        appendIfValue(formData, "ship_id", shipId);
        appendIfValue(formData, "machinery_id", machineryId);

        res = await post(ApiURL.staff_view_completed_work_order, formData);
        const completionRecord = getResponseRecord(res);
        let jobRecord = null;

        if (jobId || jobCode) {
          const jobFormData = new FormData();

          if (jobId) {
            jobFormData.append("job_id", jobId);
          } else {
            jobFormData.append("code", jobCode);
          }

          appendIfValue(jobFormData, "company_id", companyId);
          appendIfValue(jobFormData, "ship_id", shipId);
          appendIfValue(jobFormData, "machinery_id", machineryId);

          const jobRes = await post(ApiURL.staff_list_job, jobFormData);
          const jobResponseRecord = getResponseRecord(jobRes);

          jobRecord = getSingleRecord(jobResponseRecord, (item) => {
            if (jobId) return String(item?.id) === String(jobId);
            return String(item?.code || "").startsWith(String(jobCode));
          });
        }

        record = completionRecord
          ? {
            ...passedWorkOrder,
            ...jobRecord,
            ...completionRecord,
            code: passedWorkOrder?.code || completionRecord?.code || jobRecord?.code,
            title: passedWorkOrder?.title || passedWorkOrder?.job_title || jobRecord?.title || completionRecord?.title,
            job_title: passedWorkOrder?.job_title || passedWorkOrder?.title || jobRecord?.title || completionRecord?.job_title,
            attachments: completionRecord?.job_attachments || completionRecord?.attachments || jobRecord?.attachments || passedWorkOrder?.attachments || [],
            work_order_attachments: completionRecord?.work_order_attachments || [],
          }
          : null;
      } else {
        formData.append("job_id", jobId || id);
        appendIfValue(formData, "company_id", companyId);
        appendIfValue(formData, "ship_id", shipId);
        appendIfValue(formData, "machinery_id", machineryId);

        res = await post(ApiURL.staff_list_job, formData);
        record = getResponseRecord(res);
      }

      record = getSingleRecord(
        record,
        (item) => String(item?.id) === String(jobId || id)
      );

      setselectedJobList(record);

      form.setFieldsValue({
        actual_completion_date:
          record?.actual_completion_date
            ? dayjs(record.actual_completion_date)
            : null,

        completion_counter:
          record?.current_counter_at_completion || "",

        downtime_hours:
          record?.downtime_hours || "",

        total_duration_hours:
          record?.total_duration_hours || "",

        overdue_reason:
          record?.overdue_reason || "",

        work_history:
          record?.work_history || "",

        resources:
          record?.resources_used?.length
            ? record.resources_used.map((item) => ({
              discipline:
                item?.discipline || "",
              hours_spent:
                item?.hours || "",
            }))
            : [
              {
                discipline: "",
                hours_spent: "",
              },
            ],

        stock_used:
          record?.stock_used?.length
            ? record.stock_used.map((item) => ({
              part_no:
                item?.part_no || "",

              initial_rob:
                item?.initial_rob || "",

              consumed:
                item?.consumed || "",

              final_rob:
                item?.final_rob || "",

              remarks:
                item?.remarks || "",
            }))
            : [
              {
                part_no: "",
                initial_rob: "",
                consumed: "",
                final_rob: "",
                remarks: "",
              },
            ],
        attachments: [],
      });
    } catch (error) {
      console.error(error);
      ErrorToast("Error", "Failed to fetch work order");
    } finally {
      setLoading(false);
    }
  }, [companyId, form, id, isViewMode, jobCode, jobId, machineryId, passedWorkOrder, shipId, workOrderId]);

  useEffect(() => {
    fetchWorkOrder();
  }, [fetchWorkOrder]);

  const isCounterJob = (record) => {
    const type =
      record?.frequency_type ||
      record?.task_type ||
      record?.maintenance_type;

    return type === "running_hours" || type === "Hour";
  };

  const selectedDueDate =
    selectedJobList?.next_due_date || selectedJobList?.due_date;

  const actualCompletionDate = Form.useWatch(
    "actual_completion_date",
    form
  );

  const requiresOverdueReason =
    isOverdue(selectedJobList) ||
    (selectedDueDate && actualCompletionDate
      ? dayjs(actualCompletionDate).isAfter(dayjs(selectedDueDate), "day")
      : false);

  // 🔹 Submit Handler
  const handleSubmit = async (values) => {
    setSubmitLoader(true);

    try {
      const FORM_DATA = new FormData();

      FORM_DATA.append(
        "work_order_id",
        String(workOrderId)
      );

      appendIfValue(FORM_DATA, "machinery_id", machineryId);

      FORM_DATA.append(
        "actual_completion_date",
        values.actual_completion_date
          ? dayjs(values.actual_completion_date).format("YYYY-MM-DD")
          : ""
      );

      FORM_DATA.append(
        "current_counter_at_completion",
        values.completion_counter || ""
      );

      FORM_DATA.append(
        "downtime_hours",
        values.downtime_hours || ""
      );

      FORM_DATA.append(
        "total_duration_hours",
        values.total_duration_hours || ""
      );

      FORM_DATA.append(
        "work_history",
        values.work_history || ""
      );

      if (requiresOverdueReason) {
        FORM_DATA.append(
          "overdue_reason",
          values.overdue_reason || ""
        );
      }

      // 🔹 Resources Used
      FORM_DATA.append(
        "resources_used",
        JSON.stringify(
          (values.resources || []).map((item) => ({
            discipline: item?.discipline || "",
            hours: Number(item?.hours_spent || 0),
          }))
        )
      );

      FORM_DATA.append(
        "stock_used",
        JSON.stringify(
          (values.stock_used || []).map((item) => {
            const initialRob = Number(item?.initial_rob || 0);
            const consumed = Number(item?.consumed || 0);

            return {
              part_no: item?.part_no || "",
              initial_rob: initialRob,
              consumed: consumed,
              final_rob: initialRob - consumed,
              remarks: item?.remarks || "",
            };
          })
        )
      );

      (values.attachments || []).forEach((file) => {
        if (file?.originFileObj) {
          FORM_DATA.append(
            "attachments",
            file.originFileObj
          );
        }
      });

      for (let pair of FORM_DATA.entries()) {
        console.log(pair[0], pair[1]);
      }

      const res = await post(
        ApiURL.staff_complete_work_order,
        FORM_DATA
      );


      if (res?.status === true) {
        SuccessToast(
          "Success",
          "Work Order Completed Successfully"
        );

        form.resetFields();

        navigate(-1);
      } else {
        ErrorToast(
          "Error",
          res?.message || "Failed to complete work order"
        );
      }
    } catch (error) {
      console.log("completeWorkOrderError", error);

      ErrorToast(
        "Error",
        "Something went wrong"
      );
    } finally {
      setSubmitLoader(false);
    }
  };

  // 🔹 Loading State
  if (loading) {
    return (
      <div style={{ textAlign: "center", marginTop: 100 }}>
        <Spin />
      </div>
    );
  }

  // 🔹 No Data State
  if (!selectedJobList) {
    return (
      <Empty description="No work orders found" />
    );
  }

  const summaryCode =
    passedWorkOrder?.code || selectedJobList?.code || "N/A";
  const summaryTitle =
    passedWorkOrder?.job_title ||
    passedWorkOrder?.title ||
    selectedJobList?.job_title ||
    selectedJobList?.title ||
    "N/A";
  const summaryComponent =
    passedWorkOrder?.component_name ||
    passedWorkOrder?.component ||
    passedWorkOrder?.machinery_name ||
    selectedJobList?.component_name ||
    selectedJobList?.component ||
    selectedJobList?.machinery_name ||
    "N/A";
  const summaryDueDate =
    passedWorkOrder?.due_date ||
    passedWorkOrder?.next_due_date ||
    selectedJobList?.due_date ||
    selectedJobList?.next_due_date;
  const summaryFrequencyNumber =
    passedWorkOrder?.frequency_number || selectedJobList?.frequency_number;
  const summaryFrequencyType =
    passedWorkOrder?.frequency_type || selectedJobList?.frequency_type;
  const summaryResponsible =
    passedWorkOrder?.responsible ||
    passedWorkOrder?.designation ||
    passedWorkOrder?.rank ||
    selectedJobList?.responsible ||
    selectedJobList?.designation ||
    selectedJobList?.rank ||
    "N/A";
  const summaryDescription =
    passedWorkOrder?.job_description ||
    passedWorkOrder?.description ||
    selectedJobList?.job_description ||
    selectedJobList?.description ||
    "N/A";

  return (
    <div style={{ padding: 20 }}>
      {selectedJobList && (
        <Card style={{ marginBottom: 16 }}>
          <Descriptions bordered size="small" column={2}>
            <Descriptions.Item label="Work Order">
              {summaryCode} - {summaryTitle}
            </Descriptions.Item>

            <Descriptions.Item label="Component">
              {summaryComponent}
            </Descriptions.Item>

            <Descriptions.Item label="Due Date">
              <span
                style={{
                  color: isOverdue(passedWorkOrder || selectedJobList) ? "#D91D3B" : undefined,
                }}
              >
                {formatDate(summaryDueDate)}
              </span>
            </Descriptions.Item>

            <Descriptions.Item label="Frequency">
              {summaryFrequencyNumber || "N/A"}{" "}
              {summaryFrequencyType || ""}
            </Descriptions.Item>

            <Descriptions.Item label="Responsible">
              {summaryResponsible}
            </Descriptions.Item>

            <Descriptions.Item label="Description" span={2}>
              {summaryDescription}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      {selectedJobList?.attachments?.length > 0 && (
        <Card
          title={
            <span style={{ fontWeight: 600 }}>
              Existing Attachments
            </span>
          }
          style={{
            marginBottom: 16,
            borderRadius: "8px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          }}
        >
          <Row gutter={[16, 16]}>
            {selectedJobList.attachments.map(
              (file, index) => {
                const fileUrl = getAttachmentUrl(file);

                const fileName =
                  file?.name ||
                  file?.file_name ||
                  `Attachment ${index + 1}`;

                const isImage =
                  /\.(jpg|jpeg|png|gif|webp)$/i.test(
                    fileUrl || ""
                  );

                return (
                  <Col
                    xs={24}
                    sm={12}
                    md={8}
                    lg={6}
                    key={index}
                  >
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      download={false}
                      style={{
                        border:
                          "1px solid #d9d9d9",
                        borderRadius: "8px",
                        padding: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "10px",
                        textDecoration: "none",
                        color: "#000",
                        background: "#fafafa",
                        minHeight: "70px",
                      }}
                    >
                      <span
                        style={{
                          fontSize: "24px",
                        }}
                      >
                        {isImage ? <FileImageOutlined /> : <FileTextOutlined />}
                      </span>

                      <div
                        style={{
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            fontWeight: 500,
                            overflow: "hidden",
                            textOverflow:
                              "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {fileName}
                        </div>

                        <div
                          style={{
                            fontSize: "12px",
                            color: "#888",
                          }}
                        >
                          Click to view
                        </div>
                      </div>
                    </a>
                  </Col>
                );
              }
            )}
          </Row>
        </Card>
      )}

      {/* 🔹 Reusable Form */}
      {selectedJobList && (
        <WorkOrderForm
          form={form}
          selectedJobList={selectedJobList}
          selectedWorkOrder={passedWorkOrder}
          onSubmit={handleSubmit}
          submitLoader={submitLoader}
          requiresOverdueReason={requiresOverdueReason}
          isCounterJob={isCounterJob}
          viewOnly={isViewMode}
        />
      )}
    </div>
  );
};

export default WorkOrderReport;
